import { noteSymbol, type AuthorFields, type RawField } from "@/lib/titleAuthors";

/** A field in the modal: the parsed text and chips, plus a stable id for keys and focus. */
export interface DField extends RawField {
  id: number;
}

export interface DAuthor {
  id: number;
  name: DField;
  lines: DField[];
  /** The author's email: stored as a line of the author (the last one), shown as its own field. */
  email: DField;
  notes: DField[];
}

let seq = 0;
const nextId = () => ++seq;

export const emptyField = (): DField => ({ id: nextId(), text: "", chips: [] });
const withId = (f: RawField): DField => ({ id: nextId(), text: f.text, chips: f.chips });

export const emptyAuthor = (): DAuthor => ({ id: nextId(), name: emptyField(), lines: [], email: emptyField(), notes: [] });

/** A line that is an email address, as the publisher export reads it (PublisherClasses.IsEmail). */
export const isEmailLine = (f: RawField): boolean =>
  f.chips.length === 0 && /^[^\s@{}\\]+@[^\s@{}\\]+\.[^\s@{}\\]+$/.test(f.text.trim());

/** The modal always shows at least one author, even for an empty string. */
export function draftFromAuthors(fields: AuthorFields[]): DAuthor[] {
  if (fields.length === 0) return [emptyAuthor()];
  return fields.map((a) => {
    // The first email line is the Email field; any other stays a line.
    const at = a.lines.findIndex(isEmailLine);
    return {
      id: nextId(),
      name: withId(a.name),
      lines: a.lines.filter((_, i) => i !== at).map(withId),
      email: at < 0 ? emptyField() : withId(a.lines[at]),
      notes: a.notes.map(withId),
    };
  });
}

/** The fields as they are written: the email as the author's last line. */
export function fieldsToWrite(authors: DAuthor[]): AuthorFields[] {
  return authors.map((a) => ({
    name: a.name,
    lines: a.email.text.trim() ? [...a.lines, { text: a.email.text.trim(), chips: [] }] : a.lines,
    notes: a.notes,
  }));
}

export const draftFromNotes = (notes: RawField[]): DField[] => notes.map(withId);

export interface DraftSymbols {
  title: string[];
  authors: { notes: string[] }[];
}

/**
 * The symbol each note prints with, counted across the title block: the
 * title's notes first, then each author's in reading order. Notes being typed
 * count already; empty ones are dropped on save.
 */
export function draftSymbols(titleNotes: DField[], authors: DAuthor[]): DraftSymbols {
  let n = 0;
  const title = titleNotes.map(() => noteSymbol(n++));
  return { title, authors: authors.map((a) => ({ notes: a.notes.map(() => noteSymbol(n++)) })) };
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
}

export interface DuplicateNote {
  /** Who carries the same note, in reading order. */
  names: string[];
  text: string;
}

/** Notes whose text is the same across authors: LaTeX cannot share a \thanks. */
export function duplicateNotes(authors: DAuthor[]): DuplicateNote[] {
  const byText = new Map<string, string[]>();
  authors.forEach((a, i) => {
    const who = a.name.text.trim() || `Author ${i + 1}`;
    const seen = new Set<string>();
    for (const n of a.notes) {
      const t = n.text.trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      byText.set(t, [...(byText.get(t) ?? []), who]);
    }
  });
  return [...byText.entries()].filter(([, names]) => names.length > 1).map(([text, names]) => ({ names, text }));
}

export function duplicateMessage(d: DuplicateNote): string {
  const list = d.names.length === 2 ? d.names.join(" and ") : `${d.names.slice(0, -1).join(", ")} and ${d.names.at(-1)}`;
  const times = d.names.length === 2 ? "twice" : `${d.names.length} times`;
  return `${list} have the same note. It prints ${times}, once under each symbol.`;
}

/** "\orcidlink{0000-…}" for the tooltip: the command and the start of its argument. */
export function chipHint(raw: string): string {
  const open = raw.indexOf("{");
  const short = open < 0 || raw.length - open <= 8 ? raw : `${raw.slice(0, open + 6)}…}`;
  return `${short} prints as written. Edit as LaTeX to change it.`;
}
